import {
  ClickAwayListener,
  Grow,
  InputAdornment,
  Paper,
  Popper,
  TextField,
} from "@mui/material";
import { ReactComponent as CalendarIcon } from "components/Icons/calendar.svg";
import { Field, getIn } from "formik";
import { useMessage } from "hooks/intl";
import { ReactElement, useRef, useState } from "react";
import { Calendar } from "./Calendar/Calendar";
import { useStyles } from "./styles";

interface Props {
  name: string;
  label: string | ReactElement;
  placeholder?: string;
  disabled?: boolean;
}

export const DayMonthInput = ({
  name,
  label,
  placeholder,
  disabled,
}: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const classes = useStyles();
  const anchorRef = useRef<HTMLInputElement | HTMLDivElement>(null);
  const { message } = useMessage();

  const months = [
    {
      name: message({ id: "january" }),
      daysCount: 31,
      code: "01",
    },
    {
      name: message({ id: "february" }),
      daysCount: 29,
      code: "02",
    },
    {
      name: message({ id: "march" }),
      daysCount: 31,
      code: "03",
    },
    {
      name: message({ id: "april" }),
      daysCount: 30,
      code: "04",
    },
    {
      name: message({ id: "may" }),
      daysCount: 31,
      code: "05",
    },
    {
      name: message({ id: "june" }),
      daysCount: 30,
      code: "06",
    },
    {
      name: message({ id: "july" }),
      daysCount: 31,
      code: "07",
    },
    {
      name: message({ id: "august" }),
      daysCount: 31,
      code: "08",
    },
    {
      name: message({ id: "september" }),
      daysCount: 30,
      code: "09",
    },
    {
      name: message({ id: "october" }),
      daysCount: 31,
      code: "10",
    },
    {
      name: message({ id: "november" }),
      daysCount: 30,
      code: "11",
    },
    {
      name: message({ id: "december" }),
      daysCount: 31,
      code: "12",
    },
  ];

  return (
    <Field name={name}>
      {({ field, form, meta }: { field: any; form: any; meta: any }) => {
        const isError = meta.error || getIn(form.status, field.name);
        const { setFieldValue } = form;
        const { value } = field;
        const [month, day] = value ? value.split("-") : [];
        const valueLabel = value
          ? `${day} ${months[Number(month) - 1].name}`
          : "";

        return (
          <>
            <TextField
              disabled={disabled}
              ref={anchorRef}
              className={classes.input}
              placeholder={placeholder}
              InputLabelProps={{
                shrink: isOpen || !!field.value,
              }}
              label={label}
              value={valueLabel}
              onClick={(event) => {
                setIsOpen(!isOpen);
                !disabled && setIsOpen(!isOpen);
              }}
              fullWidth
              helperText={isError && Object.values(isError)}
              error={isError}
              InputProps={{
                className: classes.inputBase,
              }}
              FormHelperTextProps={{
                className: classes.helperText,
              }}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="start">
                      <CalendarIcon />
                    </InputAdornment>
                  ),
                  readOnly: true,
                  autoComplete: "off",
                },
              }}
              data-test-id="calendar-input"
            />
            <Popper
              open={isOpen}
              anchorEl={anchorRef.current}
              placement="bottom-start"
              transition
              disablePortal
              sx={{
                zIndex: 2,
              }}
            >
              {({ TransitionProps, placement }) => (
                <Grow
                  {...TransitionProps}
                  style={{
                    transformOrigin:
                      placement === "bottom-start" ? "left top" : "left bottom",
                  }}
                >
                  <Paper>
                    <ClickAwayListener
                      onClickAway={(event) => {
                        setIsOpen(false);
                      }}
                    >
                      <Calendar
                        setValue={(day: string) => {
                          setFieldValue(field.name, day);
                          setIsOpen(false);
                        }}
                        value={
                          value
                            ? {
                                day: Number(day),
                                month: Number(month) - 1,
                              }
                            : undefined
                        }
                        closeModal={() => setIsOpen(false)}
                        months={months}
                      />
                    </ClickAwayListener>
                  </Paper>
                </Grow>
              )}
            </Popper>
          </>
        );
      }}
    </Field>
  );
};
